import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { File, getFileSecureLink, downloadFile, deleteFile } from "@/api/files";
import { DataGrid, DataGridColumn } from "@/ui-controls/DataGrid";
import {
  ServerDataGrid,
  ServerDataGridColumn,
} from "@/ui-controls/ServerDataGrid";
import MoreActionsButton from "@/ui-controls/MoreActionsButton";
import SecureLinkModal from "@/components/files/SecureLinkModal";
import { 
  Link, 
  Spinner, 
  SpinnerSize, 
  Dialog, 
  DialogType, 
  DialogFooter, 
  PrimaryButton, 
  DefaultButton 
} from "@fluentui/react";

interface Props {
  files: File[];
  loading?: boolean;
  pageSize?: number;
  currentPage?: number;
  totalPages?: number;
  totalCount?: number;
  onPageSizeChange?: (pageSize: number) => void;
  onPageChange?: (page: number) => void;
  onRefresh?: () => void;
  onDelete?: (file: File) => void;
  onDownload?: (file: File) => void;
  onShowTags?: (file: File) => void;
  isServerPagination?: boolean;
}

enum MenuActionType {
  DELETE_FILE = 1,
  DOWNLOAD_FILE = 2,
}

const FileDetailList = (props: Props) => {
  const navigate = useNavigate();
  const [selectedFile, setSelectedFile] = useState<File | undefined>(undefined);
  const [menuActionType, setMenuActionType] = useState<
    MenuActionType | undefined
  >(undefined);
  const [isSecureLinkModalOpen, setIsSecureLinkModalOpen] = useState<boolean>(false);
  const [secureLinkData, setSecureLinkData] = useState<{
    file?: File;
    link?: string;
    loading: boolean;
    error?: string;
  }>({ loading: false });

  const columns: DataGridColumn[] = [
    {
      key: "id",
      name: "Id",
      fieldName: "id",
      minWidth: 50,
      maxWidth: 80,
      isRowHeader: true,
      isResizable: true,
      isPadded: true,
    },
    {
      key: "fileName",
      name: "File Name",
      fieldName: "fileName",
      minWidth: 200,
      maxWidth: 300,
      isRowHeader: true,
      isResizable: true,
      isPadded: true,
      onRender: (item: File) => (
        <span title={item.fileName || "N/A"}>{item.fileName || "N/A"}</span>
      ),
    },
    {
      key: "externalFileId",
      name: "External File ID",
      fieldName: "externalFileId",
      minWidth: 150,
      maxWidth: 200,
      isRowHeader: true,
      isResizable: true,
      isPadded: true,
      onRender: (item: File) => (
        <span title={item.externalFileId || "N/A"}>
          {item.externalFileId || "N/A"}
        </span>
      ),
    },
    {
      key: "internalFileId",
      name: "Internal File ID",
      fieldName: "internalFileId",
      minWidth: 150,
      maxWidth: 200,
      isRowHeader: true,
      isResizable: true,
      isPadded: true,
      onRender: (item: File) => (
        item.internalFileId ? (
          <Link
            onClick={(e) => {
              e.preventDefault();
              navigate(`/files/${encodeURIComponent(item.internalFileId!)}`);
            }}
            style={{ cursor: "pointer" }}
            title={item.internalFileId}
          >
            {item.internalFileId}
          </Link>
        ) : (
          <span title="N/A">N/A</span>
        )
      ),
    },
    {
      key: "customerId",
      name: "Customer ID",
      fieldName: "customerId",
      minWidth: 100,
      maxWidth: 120,
      isRowHeader: true,
      isResizable: true,
      isPadded: true,
    },
    {
      key: "fileSizeInKb",
      name: "Size (KB)",
      fieldName: "fileSizeInKb",
      minWidth: 80,
      maxWidth: 100,
      isRowHeader: true,
      isResizable: true,
      isPadded: true,
      onRender: (item: File) => <span>{item.fileSizeInKb || 0}</span>,
    },
    {
      key: "url",
      name: "URL",
      fieldName: "url",
      minWidth: 100,
      maxWidth: 150,
      isRowHeader: true,
      isResizable: true,
      isPadded: true,
      onRender: (item: File) =>
        item.isActive ? (
          <Link
            onClick={(e) => {
              e.preventDefault();
              handleView(item);
            }}
            style={{ cursor: "pointer" }}
          >
            View
          </Link>
        ) : (
          <span style={{ color: "#a19f9d" }}>N/A</span>
        ),
    },
    {
      key: "isActive",
      name: "Status",
      fieldName: "isActive",
      minWidth: 80,
      maxWidth: 100,
      isRowHeader: true,
      isResizable: true,
      isPadded: true,
      onRender: (item: File) => (
        <span
          style={{
            color: item.isActive ? "#107c10" : "#d13438",
            fontWeight: "600",
          }}
        >
          {item.isActive ? "Active" : "Inactive"}
        </span>
      ),
    },
    {
      key: "actions",
      name: "",
      minWidth: 50,
      maxWidth: 70,
      onRender: (item: File) => (
        <MoreActionsButton
          menu={[
            {
              key: "download",
              text: "Download",
              iconProps: { iconName: "Download" },
              disabled: !item.isActive,
              onClick: () => {
                handleDownload(item);
              },
            },
            {
              key: "view",
              text: "View File",
              iconProps: { iconName: "View" },
              disabled: !item.isActive,
              onClick: () => {
                handleView(item);
              },
            },
            {
              key: "secureLink",
              text: "Secure Link",
              iconProps: { iconName: "Lock" },
              disabled: !item.isActive,
              onClick: () => {
                handleSecureLink(item);
              },
            },
            {
              key: "tags",
              text: "Tags",
              iconProps: { iconName: "Tag" },
              disabled: !item.isActive,
              onClick: () => {
                if (props.onShowTags) {
                  props.onShowTags(item);
                }
              },
            },
            {
              key: "delete",
              text: "Delete",
              iconProps: { iconName: "Delete" },
              disabled: !item.isActive,
              onClick: () => {
                setMenuActionType(MenuActionType.DELETE_FILE);
                setSelectedFile(item);
              },
            },
          ]}
        />
      ),
    },
  ];

  const handleView = async (file: File) => {
    try {
      const response = await downloadFile(file.id);
      if (response && response.data) {
        // Create a blob URL for viewing
        const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/octet-stream' });
        const blobUrl = URL.createObjectURL(blob);
        window.open(blobUrl, "_blank");
        
        // Clean up the blob URL after a delay to allow the browser to load it
        setTimeout(() => {
          URL.revokeObjectURL(blobUrl);
        }, 1000);
      } else {
        console.warn("No data received for file:", file.id);
      }
    } catch (error) {
      console.error("Error viewing file:", file.id, error);
    }
  };

  const handleDownload = async (file: File) => {
    try {
      const response = await downloadFile(file.id);
      if (response && response.data) {
        // Create a blob from the response data
        const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/octet-stream' });
        
        // Create a temporary link element to trigger download
        const link = document.createElement('a');
        const blobUrl = URL.createObjectURL(blob);
        link.href = blobUrl;
        link.download = file.fileName || `file_${file.id}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Clean up the blob URL
        URL.revokeObjectURL(blobUrl);
      } else {
        console.warn("No data received for file:", file.id);
      }
    } catch (error) {
      console.error("Error downloading file:", file.id, error);
    }
    
    if (props.onDownload) {
      props.onDownload(file);
    }
  };

  const handleSecureLink = async (file: File) => {
    setSecureLinkData({ file, loading: true });
    setIsSecureLinkModalOpen(true);
    
    try {
      const secureUrl = await getFileSecureLink(file.id);
      if (secureUrl) {
        setSecureLinkData({ file, link: secureUrl, loading: false });
      } else {
        setSecureLinkData({ 
          file, 
          loading: false, 
          error: "No secure URL returned for this file." 
        });
      }
    } catch (error) {
      console.error("Error getting secure link for file:", file.id, error);
      setSecureLinkData({ 
        file, 
        loading: false, 
        error: "Failed to generate secure link. Please try again." 
      });
    }
  };

  const handleCloseSecureLinkModal = () => {
    setIsSecureLinkModalOpen(false);
    setSecureLinkData({ loading: false });
  };

  const handleDelete = async (file: File) => {
    try {
      await deleteFile(file.id);
      if (props.onDelete) {
        props.onDelete(file);
      }
    } catch (error) {
      console.error("Error deleting file:", file.id, error);
    } finally {
      setSelectedFile(undefined);
      setMenuActionType(undefined);
    }
  };

  if (props.loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "200px",
        }}
      >
        <Spinner size={SpinnerSize.large} label="Loading files..." />
      </div>
    );
  }

  if (props.files.length === 0) {
    return (
      <div
        style={{
          textAlign: "center",
          padding: "40px",
          color: "#605e5c",
        }}
      >
        <div style={{ fontSize: "16px", marginBottom: "8px" }}>
          No files found
        </div>
        <div style={{ fontSize: "14px" }}>Upload files to get started</div>
      </div>
    );
  }

  return (
    <>
      {props.isServerPagination ? (
        <ServerDataGrid
          items={props.files}
          columns={columns as ServerDataGridColumn[]}
          pageSize={props.pageSize}
          currentPage={props.currentPage}
          totalPages={props.totalPages}
          totalCount={props.totalCount}
          onPageSizeChange={props.onPageSizeChange}
          onPageChange={props.onPageChange}
        />
      ) : (
        <DataGrid
          items={props.files}
          columns={columns}
          pageSize={props.pageSize}
          onPageSizeChange={props.onPageSizeChange}
        />
      )}

      <Dialog
        hidden={!selectedFile || menuActionType !== MenuActionType.DELETE_FILE}
        onDismiss={() => {
          setSelectedFile(undefined);
          setMenuActionType(undefined);
        }}
        dialogContentProps={{
          type: DialogType.normal,
          title: 'Delete File',
          subText: `Are you sure you want to delete the file "${selectedFile?.fileName}"?`,
        }}
        modalProps={{
          isBlocking: false,
        }}
      >
        <DialogFooter>
          <PrimaryButton onClick={() => handleDelete(selectedFile!)} text="Delete" />
          <DefaultButton
            onClick={() => {
              setSelectedFile(undefined);
              setMenuActionType(undefined);
            }}
            text="Cancel"
          />
        </DialogFooter>
      </Dialog>

      <SecureLinkModal
        isOpen={isSecureLinkModalOpen}
        fileName={secureLinkData.file?.fileName}
        secureLink={secureLinkData.link}
        loading={secureLinkData.loading}
        error={secureLinkData.error}
        onDismiss={handleCloseSecureLinkModal}
      />
    </>
  );
};

export default FileDetailList;
